import { useCallback, useEffect, useState } from "react";
import {
  AppBreadcrumbs,
  AppButton,
  AppDialog,
  EmptyState,
  FormField,
  FormSection,
  PageHeader,
  StatusChip,
  toast,
} from "../../../../components/ui";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useNavigate, useParams } from "react-router-dom";
import { UserViewModel, UsersService } from "../../../../api";
import UserProfileForm from "../../Users/UserDetails/UserProfileForm";
import { useDispatch } from "react-redux";
import {
  startSpinner,
  stopSpinner,
} from "../../../../redux/actions/spinnerActions";
import {
  Avatar,
  Box,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import AttorneyCasesTable from "./AttorneyCasesTable";
import {
  isUserActive,
  toggleUserActive,
} from "../../Users/shared/userActiveActions";

const MySwal = withReactContent(Swal);

const surfaceSx = {
  bgcolor: "background.paper",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(27, 30, 51, 0.04)",
} as const;

export interface AttorneyDetailsProps {
  isNew?: boolean;
}

function getInitials(user: UserViewModel) {
  const first = user.firstName?.[0] ?? "";
  const last = user.lastName?.[0] ?? "";
  return `${first}${last}`.toUpperCase() || "?";
}

function AttorneyDetailsSkeleton() {
  return (
    <Box sx={{ width: "100%" }}>
      <Skeleton width={200} height={18} sx={{ mb: 1.5 }} />
      <Skeleton width={320} height={36} sx={{ mb: 1 }} />
      <Skeleton width={420} height={22} sx={{ mb: 3 }} />
      <Skeleton variant="rounded" height={280} />
    </Box>
  );
}

export default function AttorneysDetailsView(props: AttorneyDetailsProps) {
  const [user, setUser] = useState<UserViewModel | null>(null);
  const [loading, setLoading] = useState(!props.isNew);
  const [isEdit, setIsEdit] = useState(false);
  const [openSendEmailDialog, setOpenSendEmailDialog] = useState(false);
  const [emailMessage, setEmailMessage] = useState<string>();
  const [shortName, setShortName] = useState("");

  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loadUser = useCallback(() => {
    if (!id) return;
    setLoading(true);
    UsersService.getApiUsers(+id)
      .then((loaded) => {
        setUser(loaded);
        setShortName(loaded.shortCompanyName ?? "");
      })
      .catch(() => {
        setUser(null);
        toast.error("Could not load attorney.");
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    setIsEdit(props.isNew ?? false);
    if (props.isNew || !id) {
      setLoading(false);
      return;
    }
    loadUser();
  }, [id, props.isNew, loadUser]);

  const approveAttorney = () => {
    if (!user?.id) return;
    if (!shortName) {
      MySwal.fire({
        title: <strong>Short company name is required.</strong>,
        icon: "error",
      });
      return;
    }
    MySwal.fire({
      title: <strong>Confirm approval</strong>,
      html: (
        <div>
          You are about to approve Attorney {user.firstName} {user.lastName}.
          <br />
          Are you sure you want to continue?
        </div>
      ),
      icon: "question",
      showCancelButton: true,
      cancelButtonText: "No, go back",
      confirmButtonText: "Yes, approve",
      confirmButtonColor: "#ed633a",
      reverseButtons: true,
    }).then((result) => {
      if (!result.isConfirmed) return;
      dispatch(startSpinner());
      UsersService.postApiUsersApproveAttorney(user.id!, shortName)
        .then(() => {
          toast.success(
            `You have added ${user.firstName} ${user.lastName} as an attorney.`
          );
          navigate("/attorneys");
        })
        .finally(() => dispatch(stopSpinner()));
    });
  };

  const handleSendEmailClose = (success: boolean) => {
    if (success && user?.id) {
      dispatch(startSpinner());
      UsersService.putApiUsersRequestChanges(user.id, emailMessage)
        .then(() => toast.success("Email was sent to attorney!"))
        .finally(() => dispatch(stopSpinner()));
    }
    setOpenSendEmailDialog(false);
  };

  const handleUpdateShortClick = () => {
    if (!user?.id) return;
    if (!shortName) {
      toast.error("Short company name is required.");
      return;
    }

    dispatch(startSpinner());
    UsersService.putApiUsersUpdateShort(user.id, shortName)
      .then(() => {
        toast.success("Short company name updated.");
        setUser((prev) =>
          prev ? { ...prev, shortCompanyName: shortName } : prev
        );
      })
      .finally(() => dispatch(stopSpinner()));
  };

  const handleToggleActive = async () => {
    if (!user?.id) return;

    const activating = !isUserActive(user);
    const success = await toggleUserActive(user.id, {
      firstName: user.firstName,
      lastName: user.lastName,
      isActive: activating,
      onSuccess: () => {
        setUser((prev) => (prev ? { ...prev, isActive: activating } : prev));
      },
    });

    if (success && !activating) {
      navigate("/attorneys");
    }
  };

  if (props.isNew) {
    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: 920,
          mx: "auto",
          pb: 4,
        }}
      >
        <AppBreadcrumbs
          items={[
            { label: "Directory", to: "/attorneys" },
            { label: "New attorney" },
          ]}
        />
        <PageHeader title="New attorney" />
        <UserProfileForm
          user={{} as UserViewModel}
          isEdit
          isNew
          isAttorney
          embedded={false}
          allowRoleEdit={false}
          onCancelEdit={() => navigate("/attorneys")}
        />
      </Box>
    );
  }

  if (loading) {
    return <AttorneyDetailsSkeleton />;
  }

  if (!user) {
    return (
      <Box sx={{ width: "100%", pb: 3 }}>
        <AppBreadcrumbs
          items={[
            { label: "Directory", to: "/attorneys" },
            { label: "Attorney not found" },
          ]}
        />
        <PageHeader title="Attorney not found" />
        <Box sx={surfaceSx}>
          <EmptyState
            title="Attorney not found"
            description="This account may have been removed, or you may not have access."
            action={
              <AppButton
                variant="contained"
                onClick={() => navigate("/attorneys")}
              >
                Back to directory
              </AppButton>
            }
          />
        </Box>
      </Box>
    );
  }

  const attorneyTitle =
    [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
    "Attorney profile";
  const active = isUserActive(user);

  return (
    <Box sx={{ width: "100%", maxWidth: user.isPending ? 920 : "none", mx: user.isPending ? "auto" : 0, pb: 3 }}>
      <AppBreadcrumbs
        items={[
          { label: "Directory", to: "/attorneys" },
          { label: attorneyTitle },
        ]}
      />

      <PageHeader
        title={
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ minWidth: 0 }}
          >
            <Avatar
              src={user.photo?.url ?? undefined}
              alt={attorneyTitle}
              sx={{
                width: 48,
                height: 48,
                fontSize: 18,
                fontWeight: 700,
                bgcolor: "primary.main",
                flexShrink: 0,
              }}
            >
              {getInitials(user)}
            </Avatar>
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                letterSpacing: "-0.02em",
                lineHeight: 1.25,
              }}
            >
              {attorneyTitle}
            </Box>
            {active ? (
              <StatusChip size="small" label="Active" tone="dirtyGreen" />
            ) : (
              <StatusChip size="small" label="Inactive" variant="outlined" />
            )}
            {user.isPending && (
              <StatusChip size="small" label="Pending" tone="secondary" />
            )}
          </Stack>
        }
        actions={
          <>
            {!isEdit ? (
              <AppButton
                variant="contained"
                color="primary"
                startIcon={<EditOutlinedIcon />}
                onClick={() => setIsEdit(true)}
              >
                Edit profile
              </AppButton>
            ) : (
              <AppButton
                variant="outlined"
                color="inherit"
                startIcon={<CloseOutlinedIcon />}
                onClick={() => setIsEdit(false)}
              >
                Cancel edit
              </AppButton>
            )}
            {user.id && (
              <AppButton
                variant="outlined"
                color={active ? "error" : "primary"}
                startIcon={
                  active ? (
                    <PersonOffOutlinedIcon />
                  ) : (
                    <HowToRegOutlinedIcon />
                  )
                }
                onClick={handleToggleActive}
              >
                {active ? "Deactivate" : "Reactivate"}
              </AppButton>
            )}
          </>
        }
      />

      <Stack spacing={2}>
        <FormSection
          title="Agreement & approval"
          subtitle="Short company name used on attorney agreements."
          icon={<BusinessOutlinedIcon />}
        >
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-6">
              <FormField
                required
                label="Short company name (for agreement)"
                value={shortName}
                onChange={(e) => setShortName(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-auto">
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {user.isPending ? (
                  <>
                    <AppButton
                      variant="contained"
                      color="primary"
                      startIcon={<CheckCircleOutlineIcon />}
                      onClick={approveAttorney}
                    >
                      Approve
                    </AppButton>
                    <AppButton
                      variant="outlined"
                      color="inherit"
                      startIcon={<MailOutlineIcon />}
                      onClick={() => setOpenSendEmailDialog(true)}
                    >
                      Request changes
                    </AppButton>
                  </>
                ) : (
                  <AppButton
                    variant="contained"
                    color="primary"
                    onClick={handleUpdateShortClick}
                  >
                    Update short name
                  </AppButton>
                )}
              </Stack>
            </div>
          </div>
        </FormSection>

        {user.email ? (
          <UserProfileForm
            user={user}
            isEdit={isEdit}
            isAttorney
            embedded={false}
            allowRoleEdit={false}
            onCancelEdit={() => setIsEdit(false)}
            onSave={() => {
              setIsEdit(false);
              loadUser();
            }}
          />
        ) : (
          <Box sx={surfaceSx} p={2.5}>
            <Typography color="text.secondary">
              This attorney has no email on file, so the profile form cannot be
              shown.
            </Typography>
          </Box>
        )}

        {!user.isPending && (
          <FormSection
            title="Attorney cases"
            subtitle="Leads and matters associated with this attorney."
            icon={<GavelOutlinedIcon />}
          >
            <AttorneyCasesTable
              attorneyId={user.id}
              attorneyName={attorneyTitle}
              attorneyLeads={user.attorneyLeads}
            />
          </FormSection>
        )}
      </Stack>

      <AppDialog
        open={openSendEmailDialog}
        onClose={() => handleSendEmailClose(false)}
        title="Request changes"
        actions={
          <>
            <AppButton
              color="gray"
              variant="contained"
              onClick={() => handleSendEmailClose(false)}
            >
              Cancel
            </AppButton>
            <AppButton
              disabled={!emailMessage}
              color="primary"
              variant="contained"
              onClick={() => handleSendEmailClose(true)}
            >
              Send
            </AppButton>
          </>
        }
      >
        <Box sx={{ pt: 1 }}>
          <TextField
            fullWidth
            label="Email message"
            variant="outlined"
            multiline
            minRows={4}
            value={emailMessage}
            onChange={(event) => setEmailMessage(event.target.value)}
          />
        </Box>
      </AppDialog>
    </Box>
  );
}
